/*
 * Shared selected-image-shape reader + slide-capture-and-crop for Deckorate's
 * AI image features (Sketch to Slide, Photo Headshot, ...).
 *
 * Office.js PowerPoint has no per-shape "export this picture's bytes" call
 * (no `addPicture`/`getImage` on `Shape` in this project's PowerPointApi
 * surface) — the only image-export primitive is `Slide.getImageAsBase64`
 * (PowerPointApi 1.5+, already relied on by share-export/pdfExport.ts and by
 * the generic getSelectedSlideImage() helper). So the selected picture's
 * pixels are obtained by capturing the whole active slide, then cropping to
 * the selected shape's bounding rect on a canvas.
 *
 * Each feature keeps its own message copy (so "sketch image" vs "portrait
 * image" wording stays natural) and telemetry reason strings; only the
 * Office.js/canvas mechanics live here.
 */

import { EMU_PER_POINT, SLIDE_HEIGHT, SLIDE_WIDTH } from "../../../const";
import { hasPpt, ppt } from "../../../utils/formatUtils";

export type SlideImageErrorLogger = (reason: string, message: string) => void;

export interface SelectedSlideImageShape {
  readonly shapeId: string;
  /** Position/size in points, PowerPoint's native shape geometry unit. */
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

export interface SlideImageMessages {
  readonly pptOnly: string;
  readonly noSelection: string;
  readonly multiSelection: string;
  readonly notAnImage: string;
  readonly captureUnsupported: string;
  /** Noun used in generated crop-failure copy, e.g. "sketch" / "photo". */
  readonly itemLabel: string;
  /** Sketch to Slide fails fast on a malformed capture/crop (no usable
   *  dimensions, a canvas draw/encode error, or an empty crop result);
   *  Photo Headshot predates those checks and tolerates them (an empty
   *  base64 string flows through instead of throwing) — a locked-in
   *  behavior its test suite already depends on. Defaults to false so
   *  adding this shared module doesn't silently change either feature. */
  readonly strictCapture?: boolean;
}

export interface SlideImageReasons {
  readonly placeholderTypeCheck: string;
  readonly slideSizeRead: string;
  readonly slideCapture: string;
  readonly cropEncode: string;
  readonly cropEmptyResult: string;
}

/** Shape types Office.js reports for an inserted picture. */
const IMAGE_SHAPE_TYPES = new Set(["image", "picture", "linkedimage"]);

/** A picture dropped into a slide layout's photo placeholder reports shape.type
 * as "Placeholder", not "Image" — the actual contained type lives on
 * shape.placeholderFormat.containedType instead (PowerPointApi 1.8+). */
const PLACEHOLDER_SHAPE_TYPE = "placeholder";

/** Oversampling factor for a sharper capture than a 1:1 point/pixel capture would give. */
const CAPTURE_SCALE = 3;

/** Above this a pageSetup dimension can only be EMUs — mirrors slideGeometry.ts. */
const EMU_DETECTION_THRESHOLD_PTS = 10_000;

type SlideImageCapable = PowerPoint.Slide & {
  getImageAsBase64?: (opts: { width: number; height: number }) => OfficeExtension.ClientResult<string>;
};

function getErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

function isFinitePositive(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value > 0;
}

function normalizeSlideDimension(raw: number | undefined, fallback: number): number {
  if (!isFinitePositive(raw)) return fallback;
  return raw > EMU_DETECTION_THRESHOLD_PTS ? raw / EMU_PER_POINT : raw;
}

/** True if a "Placeholder"-typed shape's actual contents are a picture (PowerPointApi 1.8+;
 * on older hosts this can't be determined, so the shape is treated as not-an-image). */
async function isImagePlaceholder(shape: PowerPoint.Shape, onError: SlideImageErrorLogger, reason: string): Promise<boolean> {
  try {
    const placeholderFormat = shape.placeholderFormat;
    placeholderFormat.load("containedType");
    await shape.context.sync();
    return String(placeholderFormat.containedType ?? "").toLowerCase() === "image";
  } catch (error) {
    // containedType needs PowerPointApi 1.8+ — on older hosts this is expected, not a bug.
    onError(reason, getErrorMessage(error, "containedType unavailable"));
    return false;
  }
}

/** Read the current selection: exactly one image/picture shape, with its geometry. */
export async function readSelectedImageShape(
  messages: Pick<SlideImageMessages, "pptOnly" | "noSelection" | "multiSelection" | "notAnImage">,
  onError: SlideImageErrorLogger,
  reasons: Pick<SlideImageReasons, "placeholderTypeCheck">,
): Promise<SelectedSlideImageShape> {
  if (!hasPpt()) throw new Error(messages.pptOnly);

  return ppt().run(async (context) => {
    const selected = context.presentation.getSelectedShapes();
    selected.load("items");
    await context.sync();

    const items = selected.items ?? [];
    if (items.length === 0) throw new Error(messages.noSelection);
    if (items.length > 1) throw new Error(messages.multiSelection);

    const shape = items[0]!;
    shape.load("id,type,left,top,width,height");
    await context.sync();

    const type = String(shape.type ?? "").toLowerCase();
    if (!IMAGE_SHAPE_TYPES.has(type)) {
      if (type !== PLACEHOLDER_SHAPE_TYPE || !(await isImagePlaceholder(shape, onError, reasons.placeholderTypeCheck))) {
        throw new Error(messages.notAnImage);
      }
    }

    const { left, top, width, height } = shape;
    if (!Number.isFinite(left) || !Number.isFinite(top) || !isFinitePositive(width) || !isFinitePositive(height)) {
      throw new Error(messages.notAnImage);
    }

    return { shapeId: String(shape.id ?? ""), left, top, width, height };
  });
}

/** Read the presentation's actual slide size in points, falling back to the
 *  standard 16:9 default if pageSetup is unreadable. */
export async function readSlideSizePts(
  context: PowerPoint.RequestContext,
  onError: SlideImageErrorLogger,
  reason: string,
): Promise<{ widthPts: number; heightPts: number }> {
  try {
    const pageSetup = context.presentation.pageSetup;
    pageSetup.load("slideWidth,slideHeight");
    await context.sync();
    return {
      widthPts: normalizeSlideDimension(pageSetup.slideWidth, SLIDE_WIDTH),
      heightPts: normalizeSlideDimension(pageSetup.slideHeight, SLIDE_HEIGHT),
    };
  } catch (error) {
    onError(reason, getErrorMessage(error, "pageSetup read failed"));
    return { widthPts: SLIDE_WIDTH, heightPts: SLIDE_HEIGHT };
  }
}

/** Capture the active slide as a PNG data URL (base64, no `data:` prefix). */
async function captureActiveSlideBase64(
  widthPts: number,
  heightPts: number,
  messages: Pick<SlideImageMessages, "noSelection" | "captureUnsupported">,
  strict: boolean,
  onError: SlideImageErrorLogger,
  reason: string,
): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    ppt()
      .run(async (context) => {
        const selected = context.presentation.getSelectedSlides();
        selected.load("items");
        await context.sync();
        const slide = selected.items[0] as SlideImageCapable | undefined;
        if (!slide) {
          reject(new Error(messages.noSelection));
          return;
        }
        if (typeof slide.getImageAsBase64 !== "function") {
          reject(new Error(messages.captureUnsupported));
          return;
        }
        const result = slide.getImageAsBase64({
          width: Math.round(widthPts * CAPTURE_SCALE),
          height: Math.round(heightPts * CAPTURE_SCALE),
        });
        await context.sync();
        if (strict && !result.value) {
          reject(new Error("Slide capture returned no image data."));
          return;
        }
        resolve(result.value);
      })
      .catch((error: unknown) => {
        if (!strict) {
          reject(error);
          return;
        }
        onError(reason, getErrorMessage(error, "Slide capture failed"));
        reject(error instanceof Error ? error : new Error(getErrorMessage(error, "Slide capture failed")));
      });
  });
}

function loadImage(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Failed to decode the captured slide image"));
    img.src = dataUrl;
  });
}

/**
 * Crop the captured slide PNG down to the selected shape's rect. The shape's
 * geometry is in points; the capture is `CAPTURE_SCALE` px per point, so the
 * crop rect is derived directly rather than via a separate percentage step.
 */
async function cropToShapeRect(
  slidePngBase64: string,
  shape: SelectedSlideImageShape,
  slideWidthPts: number,
  itemLabel: string,
  strict: boolean,
  onError: SlideImageErrorLogger,
  reasons: Pick<SlideImageReasons, "cropEncode" | "cropEmptyResult">,
): Promise<{ base64: string; mimeType: string }> {
  const img = await loadImage(`data:image/png;base64,${slidePngBase64}`);
  if (strict && (!isFinitePositive(img.naturalWidth) || !isFinitePositive(img.naturalHeight))) {
    throw new Error("The captured slide image has no usable dimensions.");
  }
  // The capture's actual pixel width may differ slightly from the requested
  // size (host rounding) — derive px-per-point from what we actually got.
  const pxPerPt = img.naturalWidth / slideWidthPts;

  const cropX = Math.max(0, Math.round(shape.left * pxPerPt));
  const cropY = Math.max(0, Math.round(shape.top * pxPerPt));
  const cropW = Math.max(1, Math.min(img.naturalWidth - cropX, Math.round(shape.width * pxPerPt)));
  const cropH = Math.max(1, Math.min(img.naturalHeight - cropY, Math.round(shape.height * pxPerPt)));

  const canvas = document.createElement("canvas");
  canvas.width = cropW;
  canvas.height = cropH;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2D context unavailable");

  let dataUrl: string;
  if (!strict) {
    ctx.drawImage(img, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);
    dataUrl = canvas.toDataURL("image/png");
    return { base64: dataUrl.split(",")[1] ?? "", mimeType: "image/png" };
  }

  try {
    ctx.drawImage(img, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);
    dataUrl = canvas.toDataURL("image/png");
  } catch (error) {
    onError(reasons.cropEncode, getErrorMessage(error, `Cropping/encoding the ${itemLabel} failed`));
    throw new Error(`Cropping the selected ${itemLabel} image failed.`);
  }

  const base64 = dataUrl.split(",")[1];
  if (!base64) {
    onError(reasons.cropEmptyResult, "toDataURL produced no base64 payload");
    throw new Error(`Cropping the selected ${itemLabel} image produced no image data.`);
  }
  return { base64, mimeType: "image/png" };
}

/** Capture the selected image shape's pixels as a base64 PNG. */
export async function captureSelectedImage(
  shape: SelectedSlideImageShape,
  messages: SlideImageMessages,
  onError: SlideImageErrorLogger,
  reasons: SlideImageReasons,
): Promise<{ base64: string; mimeType: string }> {
  if (!hasPpt()) throw new Error(messages.pptOnly);

  const strict = messages.strictCapture ?? false;
  const { widthPts, heightPts } = await ppt().run((context) => readSlideSizePts(context, onError, reasons.slideSizeRead));
  const slidePng = await captureActiveSlideBase64(widthPts, heightPts, messages, strict, onError, reasons.slideCapture);
  return cropToShapeRect(slidePng, shape, widthPts, messages.itemLabel, strict, onError, reasons);
}
